import { ReactNode } from "react";
import { Box, Collapse, Drawer, Theme, useTheme } from "@mui/material";
import {
  CalendarDrawerProvider,
  CalendarList,
  RightPanelProvider,
  SettingsPanel,
  useCalendarDrawer,
  useRightPanel,
  UserNavBar,
} from "../../modules";

type UserLayoutProps = {
  children: ReactNode | ReactNode[];
  fillViewport?: boolean;
  // Rendered full-width above the letterboxed content (spans from the nav to the right edge)
  banner?: ReactNode;
};

const rightPanelSx = {
  position: {
    xs: "fixed",
    sm: "sticky",
  },
  top: 0,
  right: 0,
  alignSelf: "flex-start",
  height: "100vh",
  flexShrink: 0,
  zIndex: (theme: Theme) => theme.zIndex.drawer,
} as const;

export const UserLayout = (props: UserLayoutProps) => (
  <CalendarDrawerProvider>
    <RightPanelProvider>
      <UserLayoutInner fillViewport={props.fillViewport} banner={props.banner}>
        {props.children}
      </UserLayoutInner>
    </RightPanelProvider>
  </CalendarDrawerProvider>
);

const UserLayoutInner = (props: UserLayoutProps) => {
  const theme = useTheme();
  const calendarDrawer = useCalendarDrawer();
  const rightPanel = useRightPanel();
  return (
    <Box
      display="flex"
      flexDirection="row"
      minHeight="100vh"
      bgcolor={theme.palette.background.default}
      sx={{ colorScheme: theme.palette.mode }}
    >
      <UserNavBar />
      <Box flex="1 1 0" minWidth={0} display="flex" flexDirection="column">
        {props.banner}
        <Box width="100%" maxWidth="80rem" marginX="auto">
          <Box
            marginTop={props.banner ? 0 : "1.5rem"}
            marginX={{
              xs: "1rem",
              sm: "1.5rem",
            }}
            marginBottom={props.fillViewport ? 0 : "1.5rem"}
            paddingBottom={
              props.fillViewport
                ? 0
                : {
                    xs: "4.5rem",
                    sm: 0,
                  }
            }
            bgcolor="transparent"
          >
            {props.children}
          </Box>
        </Box>
      </Box>
      <Collapse
        in={rightPanel.activePanel === "settings"}
        orientation="horizontal"
        unmountOnExit
        sx={rightPanelSx}
      >
        <SettingsPanel />
      </Collapse>
      <Drawer
        anchor="right"
        open={calendarDrawer.isOpen}
        onClose={calendarDrawer.close}
        PaperProps={{
          sx: {
            width: {
              xs: "100%",
              sm: "51rem",
            },
          },
        }}
      >
        <CalendarList />
      </Drawer>
    </Box>
  );
};
